// External libs
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// MUI components
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// API hooks
// Internal components / hooks
import Tooltip from '~/components/CommonTooltip';
import CTB from '~/components/CTB';
import { useMFEContext } from '~/components/MFEProvider';
import useResponsive from '~/hooks/useResponsive';
import CpanelLoginDialog from '~/scenes/Hosting/scenes/HostingDetail/components/QuickLinks/CpanelLoginDialog';
import MultipleChoiceSsoDialog from '~/scenes/Hosting/scenes/HostingDetail/components/QuickLinks/MultipleChoiceSsoDialog';
import useServerEmailChoices from '~/scenes/Hosting/scenes/HostingDetail/components/QuickLinks/ServerEmailChoices';
import {
  convertDotsToHyphens,
  getSelectedHostingId,
} from '~/utils/hosting/helpers';

import { isSelfManagedDedicated } from '../../../utils/helpers';
import { DomainServiceStatus, HostingPlan } from '../../../utils/types';
import {
  REIMAGE_FAILED_STATUSES,
  REIMAGE_IN_PROGRESS_STATUSES,
} from '../../SelfManagedHosting/utils/constants';
import ActionButton from './ActionButton';

interface HeaderProps {
  mappedPlan: HostingPlan;
  featureValues: boolean[];
  serverStatus: string;
  domainServiceStatus: DomainServiceStatus;
  isHostingPlanReady: boolean;
  isPackageExpired: boolean;
}

export type ActionType =
  | 'website'
  | 'cpanelEmail'
  | 'whm'
  | 'cpanel'
  | 'fileManager'
  | 'reseller'
  | 'manage'
  | 'resellerCpanel'
  | 'adminPanel';

interface ActionConfig {
  type: ActionType;
  visible: boolean;
  variant: 'outlined' | 'contained';
  queryParams?: Record<string, string>;
  textKey: string;
}

const getActionConfigs = (
  hasCpanel = false,
  isReseller = false,
  hasAdvancedHosting = false,
  hasSites = false,
  hasWhm = false,
  isVps = false,
  hasEmailCapability = true,
  isHostingPlanReady = true,
  isSelfManagedDedi = false,
  isPlesk = false,
): ActionConfig[] => [
  {
    type: 'website',
    visible: hasSites,
    variant: 'outlined',
    queryParams: {},
    textKey: 'header.websiteBtn',
  },
  {
    type: 'whm',
    visible: hasWhm,
    variant: 'contained',
    queryParams: { app: 'whm' },
    textKey: 'header.whmBtn',
  },
  {
    type: 'adminPanel',
    visible: isPlesk && hasAdvancedHosting,
    variant: 'contained',
    textKey: 'header.adminPanelBtn',
  },
  {
    type: 'cpanel',
    visible: hasCpanel && !isReseller && !hasAdvancedHosting,
    variant: 'contained',
    textKey: 'header.cpanelBtn',
  },
  {
    type: 'cpanelEmail',
    visible: hasCpanel && !isReseller && hasEmailCapability,
    variant: 'outlined',
    queryParams: { email: 'cpanel_email' },
    textKey: 'header.cpanelEmailBtn',
  },
  {
    type: 'fileManager',
    visible: hasCpanel && !isReseller && !hasAdvancedHosting,
    variant: 'outlined',
    queryParams: { app: 'filemanager' },
    textKey: 'header.fileManagerBtn',
  },
  {
    type: 'reseller',
    visible: isReseller,
    variant: 'contained',
    queryParams: { app: 'resellerwhm' },
    textKey: 'header.resellerWhmBtn',
  },
  {
    type: 'resellerCpanel',
    visible: hasCpanel && isReseller,
    variant: 'contained',
    queryParams: { app: 'resellercpanel' },
    textKey: 'header.cPanelLoginBtn',
  },
  {
    type: 'manage',
    visible: isVps,
    variant: 'contained',
    textKey: isHostingPlanReady
      ? 'header.manageBtn'
      : isSelfManagedDedi
      ? 'header.manageDedicatedDisabled'
      : 'header.manageVpsDisabled',
  },
];

const Header = ({
  mappedPlan,
  featureValues,
  serverStatus,
  domainServiceStatus,
  isHostingPlanReady,
  isPackageExpired,
}: HeaderProps) => {
  const {
    title = '',
    status = '',
    hostingPlanId = '',
    backReference = '',
    domainName = '',
    isReseller = false,
    type = '',
    isPanel = '',
    domainsUsed,
    details: { ipAddress = '' },
  } = mappedPlan;
  const { t } = useTranslation('hosting', { keyPrefix: 'hostingNova' });
  const { isTablet } = useResponsive();
  const isHeaderCompact = isTablet();
  const [
    canUpgrade,
    hasCpanel,
    hasAdvancedHosting,
    hasWhm,
    hasSites,
    hasEmailCapability,
  ] = featureValues;
  const [isCpanelLoginDialogOpen, setIsCpanelLoginDialogOpen] = useState(false);
  const [serverEmailIsOpen, setServerEmailIsOpen] = useState(false);
  const [choices, setChoices] = useState<any[]>([]);
  const [isLoadingChoices, setIsLoadingChoices] = useState(false);
  const [shouldFetchChoices, setShouldFetchChoices] = useState(false);
  const fetchedChoices = useServerEmailChoices(
    hostingPlanId || '',
    shouldFetchChoices,
    hasCpanel,
  );

  useEffect(() => {
    if (!shouldFetchChoices || fetchedChoices.length === 0) return;
    setChoices((prev) => {
      const changed = JSON.stringify(prev) !== JSON.stringify(fetchedChoices);
      if (changed) setIsLoadingChoices(false);
      return changed ? fetchedChoices : prev;
    });
  }, [fetchedChoices, shouldFetchChoices]);

  const hostingId = getSelectedHostingId(hostingPlanId, backReference);
  const navigate = useNavigate();
  const {
    // @ts-expect-error
    updateMFEContext,
  } = useMFEContext();
  const isVps = type === 'vps';
  const isPlesk = isPanel === 'plesk';
  const isSelfManagedDedi = isSelfManagedDedicated(type, title);
  const isVpsCPanel = type === 'vps_cpanel';
  const isServerOnline = serverStatus === 'active';
  const isReImaging =
    REIMAGE_IN_PROGRESS_STATUSES.includes(serverStatus) ||
    REIMAGE_FAILED_STATUSES.includes(serverStatus);
  const isDisableUpgrade = REIMAGE_IN_PROGRESS_STATUSES.includes(serverStatus);
  const disableUpgrade =
    (isVps || isVpsCPanel) && (!isServerOnline || isDisableUpgrade);
  const handleServerEmailClose = (data?: string) => {
    data === 'userMissing' && setIsCpanelLoginDialogOpen(true);
    setServerEmailIsOpen(false);
  };

  const handleCpanelDialogClose = () => {
    setIsCpanelLoginDialogOpen(false);
  };
  const handleServerEmailClick = () => {
    setServerEmailIsOpen(true);
    setShouldFetchChoices(true);
  };

  const handleUpdateHostingId = (id?: string) => {
    const hostingId = getSelectedHostingId(id, backReference);
    updateMFEContext(hostingId);
    if (type === 'vps') {
      navigate(
        `/${convertDotsToHyphens(hostingId!)}/self-managed-hosting/overview`,
      );
    } else {
      navigate(`/package/websites?packageId=${hostingId}`);
    }
  };

  const actionConfigs = useMemo(() => {
    return getActionConfigs(
      hasCpanel,
      isReseller,
      hasAdvancedHosting,
      hasSites,
      hasWhm,
      isVps,
      hasEmailCapability,
      isHostingPlanReady,
      isSelfManagedDedi,
      isPlesk,
    ).sort((a, b) => (a.type === 'cpanel' ? 1 : b.type === 'cpanel' ? -1 : 0));
  }, [
    hasCpanel,
    isReseller,
    hasAdvancedHosting,
    hasSites,
    hasWhm,
    isVps,
    hasEmailCapability,
    isHostingPlanReady,
    isSelfManagedDedi,
    isPlesk,
  ]);

  const buttonProps = {
    fullWidth: isHeaderCompact,
    fluid: isHeaderCompact,
  };

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      sx={{ gap: { md: 2 } }}
    >
      <Grid item xs={12} md="auto">
        <Stack direction="column" spacing={1} alignItems="flex-start">
          <Stack
            direction={isHeaderCompact ? 'column' : 'row'}
            alignItems={isHeaderCompact ? 'flex-start' : 'center'}
            spacing={1}
          >
            <Tooltip title={title} arrow placement="top">
              <Typography
                variant="h3"
                noWrap
                sx={{ maxWidth: 350, cursor: 'default' }}
              >
                {title || t('planTitle')}
              </Typography>
            </Tooltip>

            {status === 'active' && canUpgrade && (
              <CTB
                product="HOSTING_UPGRADE"
                data={{ prodInstId: backReference, productName: title }}
                disabled={disableUpgrade}
                sx={{ textDecoration: 'none' }}
              >
                {/* @ts-ignore */}
                <Typography
                  variant="body2"
                  textTransform="uppercase"
                  fontWeight="bold"
                >
                  {t('header.upgrade')}
                </Typography>
              </CTB>
            )}
          </Stack>
          {domainName && <Typography variant="body2">{domainName}</Typography>}
        </Stack>
      </Grid>

      <Grid item xs={12} md="auto">
        <Stack
          direction={isHeaderCompact ? 'column' : 'row'}
          spacing={isHeaderCompact ? 2 : 1}
          alignItems="center"
          sx={{ mt: isHeaderCompact ? 3 : 0 }}
        >
          {actionConfigs
            .filter((cfg: ActionConfig) => cfg.visible)
            .map((cfg: ActionConfig) => (
              <ActionButton
                key={cfg.type}
                config={cfg}
                status={status ?? ''}
                hostingPlanId={hostingId ?? ''}
                onOpenServerEmail={handleServerEmailClick}
                onUpdateHostingId={handleUpdateHostingId}
                buttonProps={buttonProps}
                loading={isLoadingChoices}
                isServerOnline={isServerOnline || isReImaging}
                canAccessEmail={domainServiceStatus.canAccessEmail}
                domainsUsed={domainsUsed}
                isHostingPlanReady={isHostingPlanReady}
                isPackageExpired={isPackageExpired}
                hostingPlanType={type}
              />
            ))}
        </Stack>
      </Grid>

      <MultipleChoiceSsoDialog
        isOpen={serverEmailIsOpen}
        onClose={handleServerEmailClose}
        title={t('serverEmail.title')}
        description={t('serverEmail.description')}
        choices={choices}
        hostingId={hostingPlanId || ''}
      />
      <CpanelLoginDialog
        isOpen={isCpanelLoginDialogOpen}
        title={t('cPanelLogin.title')}
        ipAddress={ipAddress}
        description={t('cPanelLogin.description')}
        backAction={handleCpanelDialogClose}
      />
    </Grid>
  );
};

export default Header;
